import { Sponsorstripe } from '@sch-inventory/advertory/sites/aftenposten/index.js';
import React, { useEffect, useRef } from 'react';

import type { ListPageProps } from './index.js';

import { useSessionData } from '../../../../../public-src/core/js/schibsted-account/react.server.js';
import { LinkWithPulse } from '../../../../core/mandatory-integrations/pulse/components/LinkWithPulse.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import Ad from '../../components/ads/Ad/Ad.js';
import Footer from '../../components/commonComponents/Footer.js';
import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';
import Teaser from '../../components/teasers/Teaser.js';
import TeaserList from '../../components/teasers/TeaserList.js';
import useInitializeAds from '../../hooks/useInitAds.js';
import RestaurantsWidget from '../front/RestaurantsWidget.js';
import { buildFaq } from './buildFaq.js';
import { ListDescription } from './ListDescription.js';
import ListPageFaq from './ListPageFaq.js';
import { insertAds } from './utils/ads.js';
import { getRelatedTopLists } from './utils/related-top-list.js';

const ListPageV1: React.FC<ListPageProps & { isTopList: boolean }> = ({
  data,
  options,
  isTopList,
}) => {
  const { siteVersion, currentUrl } = options;
  const { properties, items } = data;
  const { faq } = properties;

  const isMobile = siteVersion === 'phone';

  const ref = useRef<HTMLDivElement>(null);

  const { trackViewListing, getEnvironmentId } = usePulseContext();

  const session = useSessionData();

  useInitializeAds(getEnvironmentId, session, siteVersion);

  useEffect(() => {
    trackViewListing({
      object: {
        id: `list-page:${isTopList ? 'top-list' : 'master-list'}`,
        name: `list page - ${isTopList ? 'top list' : 'master list'}`,
        'spt:custom': {
          currentUrl,
          ...(isTopList ? { topListVersion: 1 } : {}),
        },
      },
    });
  }, [trackViewListing, currentUrl, data, isTopList]);

  const builtFaqs = faq && buildFaq(faq);

  const relatedTopLists = getRelatedTopLists(data.properties);

  const itemsWithAds = items ? insertAds(items, isMobile) : [];

  return (
    <ListsProvider>
      <div ref={ref} className={`${siteVersion}-front`}>
        <Navigation fullWidth={!isMobile} activeSection="restaurantguiden" />
        <Sponsorstripe placementId="sponsorstripe" />
        <div className="tag-page-container">
          <div className="title-description">
            <h1 className="story-title">{properties.h1}</h1>
            <ListDescription
              className="description"
              text={properties.description}
            />
          </div>
          <div className="story-controls">
            <div className="story-hits">{`${items?.length} ${isTopList ? 'anmeldelser' : 'lister'}`}</div>
          </div>
          <div className="tag-articles-all">
            {itemsWithAds?.map((item) => {
              if (item.type === 'teaser') {
                return (
                  <Teaser
                    {...item}
                    key={item.id}
                    size={isMobile ? 'big' : 'medium'}
                    mobile={isMobile}
                    fullWidthTeaser={false}
                    lazyLoad={false}
                    colorPalette={undefined}
                    hasGroups={false}
                  />
                );
              }
              if (item.type === 'ad') {
                return (
                  <Ad
                    key={`Ad-${item.id}`}
                    id={item.id}
                    cssClass={item.cssClass}
                    isMobile={isMobile}
                    containerClass="ad-list"
                  />
                );
              }

              return (
                <TeaserList
                  {...item}
                  key={item.id}
                  mobile={isMobile}
                  size="medium"
                  slug=""
                />
              );
            })}
          </div>
          {relatedTopLists.length > 0 && (
            <RestaurantsWidget
              data={{
                title: 'Topplister fra våre anmeldere',
                items: relatedTopLists,
                properties,
              }}
              isMobile={isMobile}
            />
          )}
          {data.properties.faq && builtFaqs && (
            <ListPageFaq
              faqs={builtFaqs}
              listId={options.requestParams.childId}
            />
          )}
          <LinkWithPulse
            className="homescreen-button-centered"
            href="/"
            id="redirect-to-homescreen"
            name="Redirect to homescreen"
          >
            Gå til Vink-forsiden
          </LinkWithPulse>
        </div>
        <Footer isMobile={isMobile} />
      </div>
    </ListsProvider>
  );
};

export { ListPageV1 };
